import React, { useEffect } from 'react';
import size from 'lodash/size';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';

import VerticalCard from '../Components/VerticalCard';
import PlaceholderCard from '../Components/VerticalCard/PlaceholderCard';
import HorizontalCard from '../Components/HorizontalCard';
import CommunitiesList from '../Components/CommunitiesList';
import Loader from '../../common/Loader';
import EmptyContent from '../../common/EmptyState/Content';

import WithLazyLoad from '../../../hocs/withLazyLoad';
import { COMMUNITIES, ORGANIZATION, DISCOVER } from '../../../constants/routes';
import {
  COMMUNITIES_COMMON,
  COMMUNITIES_EMPTY,
  DIALOG_TITLE,
} from '../../../constants/strings';
import {
  DISCOVERY_LIST_TYPES,
  COMMUNITY_BTN_ACTIONS,
  ORGANIZATION_COMMUNITIES_NUMBER_MAX,
} from '../../../constants';
import { useInjectSaga } from '../../../redux/injectSaga';
import { RESTART_ON_REMOUNT } from '../../../redux/sagaInjectors';
import updateCurrentCommunity from '../../../redux/communities/sagas/updateCurrentCommunity';
import getButtonInfo from '../../../utils/communities/getButtonInfo';

import { enhance } from './enhance';

const DiscoverCommunities = ({
  getAllCommunities,
  organizationCommunitiesList,
  organizationCommunitiesCount,
  loadNextCommunities,
  reqJoinCommunity,
  acceptInvite,
  globalCommunitiesCount,
  globalCommunitiesList,
  sendingRequest,
  intl: { formatMessage },
}) => {
  useInjectSaga({
    key: 'updateCurrentCommunity',
    saga: updateCurrentCommunity,
    mode: RESTART_ON_REMOUNT,
  });

  useEffect(() => {
    getAllCommunities({ type: DISCOVERY_LIST_TYPES.ORGANIZATION });
    getAllCommunities({ type: DISCOVERY_LIST_TYPES.GLOBAL, limit: 12 });
  }, []);

  const handleSelect = id => {
    const route = `/${COMMUNITIES}/${id}`;
    browserHistory.push(route);
  };

  const loadMore = () => {
    loadNextCommunities({ type: DISCOVERY_LIST_TYPES.GLOBAL });
  };

  const handleJoinCommunity = ({ id, memberInvitationId }, actionType) => {
    if (actionType === COMMUNITY_BTN_ACTIONS.ACCEPT) {
      acceptInvite({
        id: memberInvitationId,
        actionType: COMMUNITY_BTN_ACTIONS.ACCEPT,
      });
      return;
    }
    reqJoinCommunity({ communityId: id });
  };

  const organizationCommunitiesNumber = size(organizationCommunitiesList);
  const defaultCardsNumber =
    organizationCommunitiesNumber > ORGANIZATION_COMMUNITIES_NUMBER_MAX
      ? ORGANIZATION_COMMUNITIES_NUMBER_MAX - 1
      : ORGANIZATION_COMMUNITIES_NUMBER_MAX;
  const organizationPreview =
    organizationCommunitiesList &&
    organizationCommunitiesList.slice(0, defaultCardsNumber);
  const placeholderCounter =
    organizationCommunitiesCount - size(organizationPreview);

  return (
    <WithLazyLoad loadMore={loadMore}>
      <div styleName="wrapper">
        <If condition={!!organizationCommunitiesCount}>
          <CommunitiesList
            title={formatMessage(COMMUNITIES_COMMON.fromOrganization)}
            counter={organizationCommunitiesCount}
          >
            {organizationPreview.map(community => {
              const buttonInfo = getButtonInfo(community, formatMessage);

              return (
                <VerticalCard
                  key={community.id}
                  banner={get(community, 'banner.url')}
                  logo={get(community, 'logo.url')}
                  name={community.name}
                  organization={get(community, 'organization.name')}
                  totalPosts={community.totalPosts}
                  totalUsers={community.totalUsers}
                  onClick={() => handleSelect(community.id)}
                  buttonText={formatMessage(DIALOG_TITLE.join)}
                  joinCommunity={() =>
                    handleJoinCommunity(community, buttonInfo.type)
                  }
                  buttonInfo={buttonInfo}
                />
              );
            })}
            <If
              condition={
                organizationCommunitiesNumber >
                ORGANIZATION_COMMUNITIES_NUMBER_MAX
              }
            >
              <PlaceholderCard
                counter={`+${placeholderCounter}`}
                text={formatMessage(COMMUNITIES_COMMON.seeAll)}
                route={`/${COMMUNITIES}/${DISCOVER}/${ORGANIZATION}`}
              />
            </If>
          </CommunitiesList>
        </If>
        <If condition={!!globalCommunitiesCount}>
          <CommunitiesList
            title={formatMessage(COMMUNITIES_COMMON.otherCommunities)}
            counter={globalCommunitiesCount}
          >
            {globalCommunitiesList.map(community => {
              const buttonInfo = getButtonInfo(community, formatMessage);

              return (
                <HorizontalCard
                  key={community.id}
                  banner={get(community, 'banner.url')}
                  logo={get(community, 'logo.url')}
                  name={community.name}
                  organization={get(community, 'organization.name')}
                  totalPosts={community.totalPosts}
                  totalUsers={community.totalUsers}
                  onClick={() => handleSelect(community.id)}
                  buttonText={formatMessage(DIALOG_TITLE.join)}
                  joinCommunity={() =>
                    handleJoinCommunity(community, buttonInfo.type)
                  }
                  buttonInfo={buttonInfo}
                />
              );
            })}
          </CommunitiesList>
        </If>
        <If
          condition={
            !sendingRequest &&
            !organizationCommunitiesCount &&
            !globalCommunitiesCount
          }
        >
          <div styleName="empty-state">
            <EmptyContent
              icon="icon-megaphone"
              text={formatMessage(COMMUNITIES_EMPTY)}
            />
          </div>
        </If>
        <If condition={sendingRequest}>
          <Loader />
        </If>
      </div>
    </WithLazyLoad>
  );
};

DiscoverCommunities.propTypes = {
  getAllCommunities: PropTypes.func.isRequired,
  loadNextCommunities: PropTypes.func.isRequired,
  reqJoinCommunity: PropTypes.func.isRequired,
  globalCommunitiesList: PropTypes.arrayOf(PropTypes.object).isRequired,
  organizationCommunitiesList: PropTypes.arrayOf(PropTypes.object).isRequired,
  globalCommunitiesCount: PropTypes.number.isRequired,
  organizationCommunitiesCount: PropTypes.number.isRequired,
  sendingRequest: PropTypes.bool.isRequired,
  acceptInvite: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
};

export default enhance(DiscoverCommunities);



// WEBPACK FOOTER //
// ./src/components/Communities/DiscoverCommunities/index.jsx